import { Link } from "react-router-dom";
import { ArrowUpRight, Lock } from "lucide-react";
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, ResponsiveContainer, Tooltip, ReferenceLine, ReferenceDot } from "recharts";
import { Reveal, Kicker } from "@/components/Reveal";
import { StudySummary } from "@/components/StudySummary";

const LAB_IMG =
  "https://images.unsplash.com/photo-1576671081837-49000212a370?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2MzR8MHwxfHNlYXJjaHwyfHxwaGFybWFjZXV0aWNhbCUyMGxhYm9yYXRvcnklMjBnbGFzc3xlbnwwfHx8fDE3ODgxMzYzNDF8MA&ixlib=rb-4.1.0&q=85";

const ATTRIBUTES = [
  { n: "01", title: "Selective adsorption", body: "Engineered surface chemistry with affinity for methylxanthine alkaloid structures (theobromine and caffeine) within the gastrointestinal tract." },
  { n: "02", title: "Targeted, not broad-spectrum", body: "A focused binding profile rather than indiscriminate adsorption, designed for a defined toxicological target." },
  { n: "03", title: "Low administration volume", body: "Formulation-friendly loading enables low-volume formats suitable for chews, suspensions, tablets and premixes." },
  { n: "04", title: "Stable & formulation-friendly", body: "Physically and chemically stable under ambient storage; compatible with standard veterinary dosage-form manufacturing." },
];

const SPECS = [
  ["Physical form", "Free-flowing engineered mineral powder"],
  ["Mode of action", "Selective adsorption · non-systemic"],
  ["Site of action", "Gastrointestinal tract; not absorbed"],
  ["Onset of binding", "Seconds (in vitro, canine gastric pH)"],
  ["pH performance", "Engineered for the canine gastric environment"],
  ["Administration profile", "Low-volume, dosage-form flexible"],
  ["Storage stability", "Ambient; formulation-stable"],
  ["Compatible formats", "Soft chews · suspensions · tablets · premixes"],
  ["Composition", "Disclosed under executed NDA only"],
];

const CHART = [
  { t: 0, v: 0 }, { t: 2, v: 43 }, { t: 5, v: 71 }, { t: 10, v: 90 },
  { t: 15, v: 98 }, { t: 20, v: 98.4 }, { t: 30, v: 98.7 }, { t: 45, v: 98.9 }, { t: 60, v: 99 },
];

export default function Technology() {
  return (
    <div data-testid="technology-page">
      <section className="hero-grid pt-32 lg:pt-40 pb-16 lg:pb-20 border-b border-line">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 grid lg:grid-cols-12 gap-12 items-end">
          <div className="lg:col-span-7">
            <Reveal y={16}>
              <Kicker index="§2">The Technology</Kicker>
              <h1 className="mt-6 font-headline font-semibold tracking-tight text-ink text-4xl sm:text-5xl lg:text-6xl leading-[1.06]">
                A mineral matrix engineered to bind methylxanthines.
              </h1>
              <p className="mt-7 text-base sm:text-lg text-smoke leading-relaxed max-w-2xl">
                Our active is a proprietary mineral adsorbent with an engineered porous structure
                and surface chemistry, designed from the ground up for the canine stomach and GI
                environment. It is supplied for use in products intended to support the veterinary
                management of methylxanthine (chocolate) ingestion in dogs.
              </p>
            </Reveal>
          </div>
          <div className="lg:col-span-5">
            <Reveal delay={0.15}>
              <div className="border border-line bg-white p-2">
                <div className="overflow-hidden aspect-[16/10]">
                  <img src={LAB_IMG} alt="High-purity pharmaceutical glass and mineral formulation vessels" className="img-clinical w-full h-full object-cover" />
                </div>
                <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-faint px-1 py-2.5">
                  Fig. 02 · Analytical characterization, Madison WI
                </p>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      <section className="py-20 lg:py-24" data-testid="technology-attributes">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <Reveal>
            <Kicker>Functional profile</Kicker>
          </Reveal>
          <div className="mt-10 grid md:grid-cols-2 gap-px bg-line border border-line">
            {ATTRIBUTES.map((a, i) => (
              <Reveal key={a.n} delay={i * 0.08} className="bg-white p-8 lg:p-10 hover:bg-pinelight/30 transition-colors duration-300">
                <p className="font-mono text-xs text-pine">{a.n}</p>
                <h3 className="mt-4 font-headline font-semibold text-xl text-ink tracking-tight">{a.title}</h3>
                <p className="mt-3 text-sm text-smoke leading-relaxed">{a.body}</p>
              </Reveal>
            ))}
          </div>
          <Reveal delay={0.12}>
            <div className="mt-12 border border-line bg-pinelight/40 p-8 lg:p-10" data-testid="technology-validation">
              <p className="font-mono text-xs uppercase tracking-[0.22em] text-pine">Independent validation</p>
              <h3 className="mt-4 font-headline font-semibold tracking-tight text-ink text-2xl lg:text-3xl">
                Proven safe and effective in gold-standard trials.
              </h3>
              <p className="mt-4 text-sm sm:text-base text-smoke leading-relaxed max-w-3xl">
                Safety and efficacy have been demonstrated in gold-standard trials conducted by
                independent third-party contract research organizations (CROs) and university
                research programs. Designed from the ground up for the canine stomach and GI
                environment, the active binds methylxanthines within seconds and performs at
                canine gastric pH.
              </p>
            </div>
          </Reveal>
          <Reveal delay={0.1}>
            <p className="mt-8 text-sm text-faint max-w-3xl leading-relaxed">
              We do not disclose composition, ingredient lists, ratios or manufacturing process.
              The active is designed to act within the gastrointestinal tract without systemic absorption.
            </p>
          </Reveal>
        </div>
      </section>

      <section className="bg-obsidian text-white py-20 lg:py-24" data-testid="technology-specs">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 grid lg:grid-cols-2 gap-12">
          <div>
            <Reveal>
              <p className="font-mono text-xs uppercase tracking-[0.22em] text-pinelight/70">Technical summary</p>
              <h2 className="mt-5 font-headline font-semibold tracking-tight text-2xl sm:text-3xl">
                Specification overview.
              </h2>
            </Reveal>
            <div className="mt-8 border border-white/10 divide-y divide-white/10">
              {SPECS.map(([k, v], i) => (
                <Reveal key={k} delay={i * 0.05} className="grid grid-cols-5 px-5 py-4 gap-4">
                  <p className="col-span-2 font-mono text-[11px] uppercase tracking-[0.15em] text-white/45 pt-0.5">{k}</p>
                  <p className="col-span-3 text-sm text-white/85">{v}</p>
                </Reveal>
              ))}
            </div>
          </div>

          <div>
            <Reveal delay={0.1}>
              <p className="font-mono text-xs uppercase tracking-[0.22em] text-pinelight/70">Adsorption profile · In vitro</p>
              <h2 className="mt-5 font-headline font-semibold tracking-tight text-2xl sm:text-3xl">
                Binding begins in seconds.
              </h2>
              <p className="mt-3 text-sm text-white/60 leading-relaxed">
                Up to 98% of theobromine bound within 15 seconds.
              </p>
            </Reveal>
            <Reveal delay={0.15} className="mt-8 border border-white/10 p-5">
              <div data-testid="technology-kinetics-chart" className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={CHART} margin={{ top: 20, right: 12, left: -10, bottom: 0 }}>
                    <CartesianGrid stroke="rgba(255,255,255,0.08)" vertical={false} />
                    <XAxis dataKey="t" stroke="rgba(255,255,255,0.35)" tick={{ fontSize: 10, fontFamily: "JetBrains Mono" }} tickLine={false} axisLine={false} />
                    <YAxis domain={[0, 100]} tickFormatter={(v) => `${v}%`} stroke="rgba(255,255,255,0.35)" tick={{ fontSize: 10, fontFamily: "JetBrains Mono" }} tickLine={false} axisLine={false} />
                    <Tooltip
                      contentStyle={{ background: "#0B1117", border: "1px solid rgba(255,255,255,0.15)", fontSize: 11, fontFamily: "JetBrains Mono" }}
                      labelStyle={{ color: "#8392A5" }}
                      itemStyle={{ color: "#E6F3EF" }}
                      formatter={(value) => [`${value}%`, "Theobromine bound"]}
                      labelFormatter={(t) => `${t} s`}
                    />
                    <ReferenceLine y={98} stroke="#3FA98C" strokeDasharray="4 4" strokeOpacity={0.55} />
                    <ReferenceDot
                      x={15}
                      y={98}
                      r={4.5}
                      fill="#3FA98C"
                      stroke="#0B1117"
                      strokeWidth={1.5}
                      label={{ value: "98% · 15 s", position: "top", fill: "#E6F3EF", fontSize: 10, fontFamily: "JetBrains Mono", offset: 10 }}
                    />
                    <Area type="monotone" dataKey="v" name="Theobromine bound" stroke="#3FA98C" fill="#0D5C46" fillOpacity={0.35} strokeWidth={1.5} />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
              <p className="mt-4 font-mono text-[10px] uppercase tracking-[0.18em] text-white/40 leading-relaxed">
                X · time (seconds) · Y · theobromine bound (%). In vitro, simulated canine gastric fluid. Up to 98% bound within 15 seconds. In vitro results; not clinical data. Full method and dataset available to qualified partners under NDA.
              </p>
            </Reveal>
          </div>
        </div>
      </section>

      <StudySummary />

      <section className="py-20 lg:py-24">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <Reveal>
            <div className="border border-line bg-white p-8 lg:p-12 grid lg:grid-cols-12 gap-8 items-center">
              <div className="lg:col-span-8">
                <p className="font-mono text-xs uppercase tracking-[0.22em] text-pine flex items-center gap-2">
                  <Lock size={13} /> Confidential data
                </p>
                <h2 className="mt-4 font-headline font-semibold tracking-tight text-ink text-2xl sm:text-3xl leading-tight">
                  Quantitative binding data under NDA.
                </h2>
                <p className="mt-4 text-sm sm:text-base text-smoke leading-relaxed max-w-2xl">
                  Adsorption capacity, kinetics and selectivity characterization are available to
                  qualified partners under executed NDA, together with formulation guidance from
                  our Madison laboratory.
                </p>
              </div>
              <div className="lg:col-span-4 lg:text-right">
                <Link
                  to="/partnering"
                  data-testid="technology-nda-cta"
                  className="inline-flex items-center gap-2 bg-pine hover:bg-pinehover text-white text-sm font-medium px-7 py-4 transition-colors duration-200"
                >
                  Qualify for data access <ArrowUpRight size={15} />
                </Link>
              </div>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
